import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import { isPrerendering, fallbackProducts, fallbackAccessories } from '../prerenderData';
import { getPricing, fmtPrice } from '../utils/pricing';
import './ProductsPage.css';
import { apiFetch } from '../services/api';
import { cardImage } from '../utils/productImages';

function SkeletonCard() {
  return (
    <div className="pp-card pp-card--skeleton">
      <div className="pp-card-img"><div className="pp-skeleton-box" style={{ width: '100%', height: '100%' }} /></div>
      <div className="pp-card-body">
        <div className="pp-skeleton-box" style={{ width: '40%', height: 12, marginBottom: 8 }} />
        <div className="pp-skeleton-box" style={{ width: '70%', height: 18, marginBottom: 8 }} />
        <div className="pp-skeleton-box" style={{ width: '90%', height: 14, marginBottom: 8 }} />
        <div className="pp-skeleton-box" style={{ width: '30%', height: 14 }} />
      </div>
    </div>
  );
}

function ProductsPage() {
  // Seeded from the build-time snapshot on the client too, so hydration
  // adopts the pre-rendered grid rather than rebuilding it from empty.
  const allFallback = [...fallbackProducts, ...fallbackAccessories];
  const [products, setProducts] = useState(allFallback);
  // The filter is URL-driven so it can be linked to. The main nav's "Pergolas"
  // item points at /products?filter=pergolas; without this it landed on the
  // unfiltered list, which shows awnings too.
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get('filter') || 'all';
  const setFilter = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'all') params.delete('filter');
    else params.set('filter', next);
    setSearchParams(params, { replace: true });
  };
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isPrerendering) return;

    apiFetch('/api/products')
      .then((r) => r.json())
      .then((prods) => {
        setProducts(prods);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // UX-18: fixed display order so the filter buttons appear in the
  // same sequence every visit. Categories not in this list are
  // appended alphabetically as a safety net for any new enum value
  // that gets added before this list is updated.
  const CATEGORY_DISPLAY_ORDER = ['louvered', 'flat-blade', 'retractable', 'awning', 'accessory'];
  // "Pergolas" isn't a stored category - the pergola range is spread across
  // three roof types. Group them so one filter (and one nav link) covers the
  // whole pergola range without pulling in awnings or accessories.
  const PERGOLA_CATEGORIES = ['louvered', 'flat-blade', 'retractable'];
  const presentCategories = new Set(products.map((p) => p.category).filter(Boolean));
  const orderedKnown = CATEGORY_DISPLAY_ORDER.filter((c) => presentCategories.has(c));
  const orderedUnknown = [...presentCategories]
    .filter((c) => !CATEGORY_DISPLAY_ORDER.includes(c))
    .sort();
  const hasPergolas = PERGOLA_CATEGORIES.some((c) => presentCategories.has(c));
  const categories = ['all', ...(hasPergolas ? ['pergolas'] : []), ...orderedKnown, ...orderedUnknown];

  const matchesFilter = (p) => {
    if (filter === 'all') return true;
    if (filter === 'pergolas') return PERGOLA_CATEGORIES.includes(p.category);
    return p.category === filter;
  };
  const filtered = products.filter(matchesFilter);

  const formatCategory = (cat) => (cat === 'pergolas'
    ? 'Pergolas'
    : cat.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '));

  return (
    <div className="products-page">
      <SEO
        title="Premium Pergola Systems & Products Melbourne"
        description="Explore our range of premium pergola systems - louvered, flat blade, retractable and fixed roof options. Custom-engineered for Melbourne homes and businesses."
        keywords="pergola systems Melbourne, louvered pergola, retractable roof, flat blade pergola, aluminium pergola products, outdoor roof systems"
        canonical="/products"
        jsonLd={[SEO.serviceSchema, SEO.breadcrumbSchema([{ name: 'Products' }]), ...(filtered.length > 0 ? [SEO.itemListSchema(filtered)] : []), SEO.webPageSchema({ type: 'CollectionPage', name: 'Premium Pergola Systems & Products Melbourne', description: 'Explore our range of premium pergola systems.', url: '/products' })]}
      />
      {/* Hero */}
      <section className="pp-hero">
        <div className="pp-hero-bg"></div>
        <div className="pp-hero-content">
          <p className="pp-label">Our Range</p>
          <h1 className="pp-title">Pergola Systems</h1>
          <p className="pp-subtitle">Precision-engineered outdoor structures designed to transform your space.</p>
        </div>
      </section>

      <Breadcrumbs items={[{ name: 'Products' }]} />

      {/* Compare link */}
      <div style={{ textAlign: 'center', padding: '20px 24px 0' }}>
        <Link to="/products/compare" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 24px', background: '#f1f5f9', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#1e293b', textDecoration: 'none', border: '1px solid var(--asg-border)', transition: 'background 0.2s' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="18" rx="1" /><rect x="14" y="3" width="7" height="18" rx="1" /></svg>
          Compare All Models
        </Link>
      </div>

      {/* Filter */}
      <section className="pp-filter">
        <div className="pp-filter-inner">
          {categories.map(cat => (
            <button
              key={cat}
              className={`pp-filter-btn ${filter === cat ? 'pp-filter-btn--active' : ''}`}
              onClick={() => setFilter(cat)}
            >
              {cat === 'all' ? 'All Products' : formatCategory(cat)}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="pp-grid-section">
        <div className="pp-grid">
          {loading ? (
            <>{[1,2,3].map(i => <SkeletonCard key={i} />)}</>
          ) : filtered.map(product => (
            <Link to={`/products/${product.slug || product._id}`} className="pp-card" key={product._id}>
              <div className="pp-card-img">
                {cardImage(product) ? (
                  <img src={cardImage(product)} alt={product.name} width={600} height={400} loading="lazy" />
                ) : (
                  <div className="pp-card-img-placeholder"></div>
                )}
              </div>
              <div className="pp-card-body">
                {product.category && (
                  <span className="pp-card-cat">{formatCategory(product.category)}</span>
                )}
                <h2 className="pp-card-name">{product.name}</h2>
                {product.short_description && (
                  <p className="pp-card-desc">{product.short_description}</p>
                )}
                {(() => {
                  const pricing = getPricing(product);
                  if (!pricing) return null;
                  return (
                    <div className="pp-card-price">
                      {pricing.original != null && (
                        <span className="pp-card-price-original">${fmtPrice(pricing.original)}</span>
                      )}
                      From ${fmtPrice(pricing.sale)}
                    </div>
                  );
                })()}
                <span className="pp-card-link">View Details →</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="pp-cta">
        <div className="pp-cta-inner">
          <h2>Not sure which system is right for you?</h2>
          <p>Send us a photo and your measurements and we'll help you find the perfect solution - no obligation.</p>
          <Link to="/contact" className="pp-cta-btn">Get a No-Obligation Quote</Link>
        </div>
      </section>
    </div>
  );
}

export default ProductsPage;
